/** Single source of truth for the per-exercise toggle options.
 *
 * These seven toggles are surfaced in three places that must agree:
 *   - the three-dot "Additional Options" menu (CalendarItemDropdownOptions)
 *   - the "Options" dropdown in the transposed table header (ExerciseOptionsMenu)
 *   - the status pills next to it (ExerciseOptionPill), whose x toggles off
 *
 * Each option id IS the DraggableContent state key, which is also the
 * onDropdownChange payload key and the tables' prop name - one identifier, no
 * mapping table.
 *
 * There are three separate "can't turn this on" concepts and collapsing them
 * would change how the three-dot menu looks:
 *   isAvailable  - the row/pill isn't rendered at all (was each item's `inActive`)
 *   isGreyedOut  - renders greyed and inert, and is skipped by arrow-key nav
 *                  in the three-dot menu (was the module-level
 *                  `isItemDisabled`). The Options dropdown deliberately lands
 *                  on these rows instead: there the row IS the explanation for
 *                  why an option is locked, and `canToggle` changes while that
 *                  menu is open, so a nav list filtered on it would shift
 *                  indices under the coach's own keypress.
 *   isLocked     - renders normally in the three-dot menu but silently does
 *                  nothing (was the early returns inside the item actions)
 * The newer Options dropdown greys both isGreyedOut and isLocked rows, and
 * neither ever gets an x on its pill.
 *
 * A fourth state, `isBlocked`, is resolved at runtime by
 * useExerciseOptionToggles rather than by a predicate here: it depends on the
 * other options' current VALUES, which the predicates above never see. An
 * option lists the ids that block it in `disabledByIds`. Blocking is not
 * clearing - unlike `exclusiveWithId` (Force PR / Disable PR), nothing is ever
 * silently switched off; whichever option is on first simply locks the other
 * until it is turned off.
 *
 * The availability rules are transcribed verbatim from the `inActive`
 * expressions that used to live inline on each menu item. Note the mixed
 * accessor - some terms test the resolved contentType, some test the raw
 * content.type - so every predicate takes both.
 */

/** Raw content.type values that hide every option. */
const isHiddenRawType = (content) => (
  content?.type === 'rest'
  || content?.type === 'questionnaire'
  || content?.type === 'video'
  || content?.type === 'warm_up'
  || content?.type === 'progression'
  || content?.type === 'P'
);

/** Lifts and tags only. Used by Each Side, both Track toggles and both PR toggles. */
const isLiftOnlyOptionAvailable = (content, contentType) => !(
  contentType === 'R'
  || contentType === 'C'
  || contentType === 'S'
  || contentType === 'N'
  || contentType === 'H'
  || contentType === 'CD'
  || contentType === 'session_break'
  || isHiddenRawType(content)
);

/** Lifts, tags and cardio/SAQ+C. Circuits are excluded because a circuit can
 * only be made completion only at creation. */
const isCompletionOnlyAvailable = (content, contentType) => !(
  contentType === 'C'
  || contentType === 'S'
  || contentType === 'N'
  || contentType === 'H'
  || contentType === 'CD'
  || contentType === 'session_break'
  || isHiddenRawType(content)
);

/** Lifts, tags, cardio/SAQ+C and circuits. */
const isCoachCompletionAvailable = (content, contentType) => !(
  contentType === 'S'
  || contentType === 'N'
  || contentType === 'H'
  || contentType === 'CD'
  || contentType === 'session_break'
  || isHiddenRawType(content)
);

/** "Completion Only" is meaningless for a cardio item whose only input already
 * is a completion, so it renders greyed out and inert. */
const isCompletionOnlyGreyedOut = (content) => (
  (content?.exercise?.type === 'R' && content?.exercise?.inputType === 'C')
  || (content?.type === 'R' && content?.preSave && content?.inputType === 'C')
);

/** Circuits can only be made completion only on creation - the three-dot menu
 * renders the row normally but the action no-ops. (Circuits are already
 * unavailable above; this keeps the guard where the behavior lives.) */
const isCompletionOnlyLocked = (content, contentType) => contentType === 'C';

/** A cardio item that already defaults to coach completion can't have it
 * toggled off - the three-dot row renders normally but no-ops. */
const isCoachCompletionLocked = (content, contentType, defaultExOptions) => (
  contentType === 'R' && !!defaultExOptions?.cardioCoach
);

const never = () => false;

/** Tooltip copy for a blocked option, shown by the Options dropdown and, for
 * Bodyweight, by the Load row's SwapPropDropdown. Exported separately because
 * SwapPropDropdown applies the block rule itself rather than going through
 * EXERCISE_OPTIONS. The three-dot menu and the pills grey blocked options
 * without a tooltip. */
export const BLOCKED_BY_COMPLETION_ONLY_REASON = 'Disabled when Completion Only on';
export const BLOCKED_BY_BODYWEIGHT_REASON = 'Disabled when Bodyweight selected';

export const EXERCISE_OPTION_IDS = {
  /** `isBodyweight`, not `bodyweight` - the id is already the variable name in
   * every table and already the key PreSaveTableTransposed reads and writes in
   * its Redux backup, so renaming it would break restore for in-flight items. */
  BODYWEIGHT: 'isBodyweight',
  TRACK_REP_COUNT: 'trackRepCount',
  TRACK_VOLUME_LOAD: 'trackVolumeLoad',
  FORCE_PR_UPDATE: 'forcePrUpdate',
  DISABLE_PR_UPDATE: 'disablePrUpdate',
  EACH_SIDE: 'isLeftAndRight',
  COMPLETION_ONLY: 'completionOnly',
  COACH_COMPLETION: 'coachCompletion',
};

/** `menuLabel` is the wording the three-dot menu has always used; `label` is
 * the shorter wording used by the newer Options dropdown and the pills.
 * Ordered as the design specifies for the Options dropdown and the pills row. */
export const EXERCISE_OPTIONS = [
  /** Unlike its neighbors, flipping this one also rewrites the table's row
   * model - the load row is renamed to Bodyweight and its prescribed values are
   * wiped. That part can't live here because only the table owns `primaryRows`
   * and `primaryRowSwitch`; each table reconciles it from the boolean. */
  {
    id: EXERCISE_OPTION_IDS.BODYWEIGHT,
    label: 'Bodyweight',
    menuLabel: 'Bodyweight',
    icon: 'muscle',
    iconObKey: 'bodyweightToggle',
    hasDisabledSlash: false,
    /** Load isn't an option in this system, so there's no partner to clear -
     * load/bodyweight exclusivity lives in `primaryRowSwitch`. */
    exclusiveWithId: null,
    /** A completion-only item has no prescription for bodyweight to modify. */
    disabledByIds: [EXERCISE_OPTION_IDS.COMPLETION_ONLY],
    blockedReason: BLOCKED_BY_COMPLETION_ONLY_REASON,
    isAvailable: isLiftOnlyOptionAvailable,
    isGreyedOut: never,
    isLocked: never,
  },
  {
    id: EXERCISE_OPTION_IDS.TRACK_REP_COUNT,
    label: 'Track Reps',
    menuLabel: 'Track Rep Count',
    icon: 'send',
    iconObKey: 'trackRepCountToggle',
    hasDisabledSlash: false,
    exclusiveWithId: null,
    disabledByIds: [],
    blockedReason: null,
    isAvailable: isLiftOnlyOptionAvailable,
    isGreyedOut: never,
    isLocked: never,
  },
  {
    id: EXERCISE_OPTION_IDS.TRACK_VOLUME_LOAD,
    label: 'Track Volume',
    menuLabel: 'Track Volume Load',
    icon: 'pr-tracking',
    iconObKey: 'trackVolumeLoadToggle',
    hasDisabledSlash: false,
    exclusiveWithId: null,
    disabledByIds: [],
    blockedReason: null,
    isAvailable: isLiftOnlyOptionAvailable,
    isGreyedOut: never,
    isLocked: never,
  },
  {
    id: EXERCISE_OPTION_IDS.FORCE_PR_UPDATE,
    label: 'Force PR',
    menuLabel: 'Force PR Update',
    icon: 'force-pr-update',
    iconObKey: 'forcePrUpdateToggle',
    hasDisabledSlash: false,
    exclusiveWithId: EXERCISE_OPTION_IDS.DISABLE_PR_UPDATE,
    disabledByIds: [],
    blockedReason: null,
    isAvailable: isLiftOnlyOptionAvailable,
    isGreyedOut: never,
    isLocked: never,
  },
  {
    id: EXERCISE_OPTION_IDS.DISABLE_PR_UPDATE,
    label: 'Disable PR',
    menuLabel: 'Disable PR Update',
    icon: 'force-pr-update',
    iconObKey: 'disablePrUpdateToggle',
    hasDisabledSlash: true,
    exclusiveWithId: EXERCISE_OPTION_IDS.FORCE_PR_UPDATE,
    disabledByIds: [],
    blockedReason: null,
    isAvailable: isLiftOnlyOptionAvailable,
    isGreyedOut: never,
    isLocked: never,
  },
  {
    id: EXERCISE_OPTION_IDS.EACH_SIDE,
    label: 'L/R Each Side',
    menuLabel: 'Each Side (Left & Right)',
    icon: 'each-side',
    iconObKey: 'eachSideToggle',
    hasDisabledSlash: false,
    exclusiveWithId: null,
    disabledByIds: [],
    blockedReason: null,
    isAvailable: isLiftOnlyOptionAvailable,
    isGreyedOut: never,
    isLocked: never,
  },
  {
    id: EXERCISE_OPTION_IDS.COMPLETION_ONLY,
    label: 'Completion Only',
    menuLabel: 'Completion Only',
    icon: 'completion-circle',
    iconObKey: 'completionOnlyToggle',
    hasDisabledSlash: false,
    exclusiveWithId: null,
    /** Mirror of Bodyweight's rule - whichever is set first blocks the other. */
    disabledByIds: [EXERCISE_OPTION_IDS.BODYWEIGHT],
    blockedReason: BLOCKED_BY_BODYWEIGHT_REASON,
    isAvailable: isCompletionOnlyAvailable,
    isGreyedOut: isCompletionOnlyGreyedOut,
    isLocked: isCompletionOnlyLocked,
  },
  {
    id: EXERCISE_OPTION_IDS.COACH_COMPLETION,
    label: 'Coach Completion',
    menuLabel: 'Coach Completion',
    icon: 'coach-completion',
    iconObKey: 'coachCompletionToggle',
    hasDisabledSlash: false,
    exclusiveWithId: null,
    disabledByIds: [],
    blockedReason: null,
    isAvailable: isCoachCompletionAvailable,
    isGreyedOut: never,
    isLocked: isCoachCompletionLocked,
  },
];

/** Which options each table variant surfaces, preserving the pill sets the
 * tables hardcoded before. Lift and tag tables show all seven; the SAQ+C and
 * circuit tables only ever showed the two completion pills. */
export const LIFT_PILL_OPTION_IDS = EXERCISE_OPTIONS.map((option) => option.id);
export const COMPLETION_PILL_OPTION_IDS = [
  EXERCISE_OPTION_IDS.COMPLETION_ONLY,
  EXERCISE_OPTION_IDS.COACH_COMPLETION,
];

/** Resolves the same `contentType` DraggableContent derives, for callers that
 * only hold `content`. Mirrors DraggableContent's `typeResolver`. */
export const resolveExerciseContentType = (content) => {
  if (content?.type === 'session_break') return 'session_break';
  if (content?.type === 'rest') return 'rest';
  if (content?.preSave) return content?.type;
  return content?.exercise?.type;
};

/** `content.preSave` items keep their defaults on the item itself; saved items
 * keep them on the resolved exercise. */
export const getDefaultExerciseOptions = (content) => (
  content?.preSave ? content?.defaultOptions : content?.exercise?.defaultOptions
);

export const getExerciseOptionDescriptor = (id) => (
  EXERCISE_OPTIONS.find((option) => option.id === id)
);
